import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Plus, Minus, Trash2, Eye, RefreshCw, Search, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { getCachedNewsData, processNewsFlags } from '../utils/newsCache';
import { renderNewsRating } from '../utils/renderNewsRating';
import { getCachedWatchlistMap, setCachedWatchlistMap, updateCachedWatchlistMap, getCachedWatchlist, getCachedWatchlistData, setCachedWatchlistDataWithDate, clearCachedWatchlistData, clearCachedWatchlistMap } from '../utils/portfolioWatchlistCache';
import { getCachedTradeDates, getLatestTradeDateKeySync } from '../utils/tradeDatesCache';
import { buildWatchlistDisplayData, computeWatchlistSummary, clearPwStockIndexCacheAndStocksMemory } from '../utils/portfolioWatchlistStockData';
import MultiSymbolAutocomplete from './MultiSymbolAutocomplete';
import { applyLiveQuotesToStocks } from '../utils/liveMarketData';
import { useLiveMarketDisplay } from '../hooks/useLiveMarketDisplay';
import { authFetch } from '../utils/authFetch';

function resolveSymbolIdFromMap(raw, symbolMap) {
  if (!raw || !symbolMap) return null;
  const t = raw.trim();
  if (symbolMap[t]) return t;
  return Object.keys(symbolMap).find((k) => k.toUpperCase() === t.toUpperCase()) || null;
}

// Align with screener default (momentum strategy)
const SIGNAL_FIELD = 'status2';
const PCT_1W_FIELD = 'pct_change_5days';
const PCT_1M_FIELD = 'pct_change_20days';
const PCT_3M_FIELD = 'pct_change_60days';
const PCT_6M_FIELD = 'pct_change_120days';

const EMPTY_WATCHLIST_SUMMARY = {
  avg6MonthPerformance: 0,
  avg1WeekPerformance: 0,
  avg1MonthPerformance: 0,
  avg3MonthPerformance: 0,
};

const Watchlist = () => {
  const MAX_WATCHLISTS = 3;
  const MAX_SYMBOLS_PER_WATCHLIST = 10;
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistMap, setWatchlistMap] = useState({});
  const [watchlistNames, setWatchlistNames] = useState([]);
  const [activeWatchlist, setActiveWatchlist] = useState(null);
  const [newSymbols, setNewSymbols] = useState('');
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(1);
  const [watchlistStocks, setWatchlistStocks] = useState([]);
  const [allTradeDates, setAllTradeDates] = useState([]);
  const [watchlistSummary, setWatchlistSummary] = useState({ ...EMPTY_WATCHLIST_SUMMARY });
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [watchlistToDelete, setWatchlistToDelete] = useState(null);
  const [newsFlags, setNewsFlags] = useState({}); // { symbol_id: ... }
  const [newsFlagRatings, setNewsFlagRatings] = useState({}); // { symbol_id: number | null }
  const [isLoadingWatchlistMap, setIsLoadingWatchlistMap] = useState(false);
  const [isLoadingWatchlist, setIsLoadingWatchlist] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [sortField, setSortField] = useState('symbol');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showManagePanel, setShowManagePanel] = useState(false);

  const itemsPerPage = 10;

  const WATCHLIST_COLUMNS = [
    { key: 'symbol', label: 'Symbol', type: 'string' },
    { key: 'name', label: 'Company', type: 'string' },
    { key: 'sector', label: 'Sector', type: 'string' },
    { key: 'market_cap', label: 'Market Cap', type: 'number' },
    { key: 'close', label: 'Price', type: 'number' },
    { key: 'pct_change', label: '% Change', type: 'number' },
    { key: 'trade_timestamp', label: 'Updated At', type: 'date' },
    { key: 'price_ext', label: 'Price Ext.', type: 'number' },
    { key: 'pct_change_ext', label: '% Change Ext', type: 'number' },
    { key: 'volume', label: 'Volume', type: 'number' },
    { key: PCT_1W_FIELD, label: '% Change 1W', type: 'number' },
    { key: PCT_1M_FIELD, label: '% Change 1M', type: 'number' },
    { key: PCT_3M_FIELD, label: '% Change 3M', type: 'number' },
    { key: PCT_6M_FIELD, label: '% Change 6M', type: 'number' },
    { key: SIGNAL_FIELD, label: 'Signal', type: 'string' },
    { key: 'beta', label: 'Beta', type: 'number' },
    { key: 'pe', label: 'PE', type: 'number' },
    { key: 'rsi', label: 'RSI', type: 'number' },
  ];

  const handleColumnSort = (fieldKey) => {
    setPage(1);
    if (sortField === fieldKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : prev === 'desc' ? 'neutral' : 'asc'));
    } else {
      setSortField(fieldKey);
      setSortDirection('asc');
    }
  };

  const compareSortValues = (a, b, type, order) => {
    const aEmpty = a == null || a === '' || a === '--';
    const bEmpty = b == null || b === '' || b === '--';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    let cmp = 0;
    if (type === 'number') {
      cmp = Number(a) - Number(b);
      if (!Number.isFinite(cmp)) cmp = 0;
    } else if (type === 'date') {
      cmp = String(a).localeCompare(String(b));
    } else {
      cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
    }
    return order === 'desc' ? -cmp : cmp;
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    setUser(storedUser);
    if (storedUser) {
      setIsLoadingWatchlistMap(true);
      const cachedMap = getCachedWatchlistMap(storedUser.email);
      if (cachedMap && Object.keys(cachedMap).length === 0) {
        clearCachedWatchlistMap(storedUser.email);
      }
      fetchWatchlistMap(storedUser.email)
        .then(() => {
          if (activeWatchlist) {
            setIsLoadingWatchlist(true);
            fetchWatchlist(storedUser.email, activeWatchlist).finally(() => setIsLoadingWatchlist(false));
          }
        })
        .finally(() => setIsLoadingWatchlistMap(false));
    }
    fetchAllTradeDates();
  }, []);

  useEffect(() => {
    // Only fetch stocks if watchlist is not empty
    if (watchlist && watchlist.length > 0 && !isUpdating) {
      fetchWatchlistStocks();
    } else if (watchlist && watchlist.length === 0) {
      // If watchlist is empty, set empty data
      setWatchlistStocks([]);
      setWatchlistSummary({ ...EMPTY_WATCHLIST_SUMMARY });
    }
  }, [watchlist, isUpdating]);

  useEffect(() => {
    if (user && activeWatchlist) {
      setIsLoadingWatchlist(true);
      fetchWatchlist(user.email, activeWatchlist).finally(() => setIsLoadingWatchlist(false));
    } else {
      setIsLoadingWatchlist(false);
    }
  }, [user, activeWatchlist]);

  // Fetch news flags for watchlist stocks
  useEffect(() => {
    if (watchlistStocks && watchlistStocks.length > 0) {
      fetchNewsFlags(watchlistStocks.map(stock => stock.symbol_id));
    } else {
      setNewsFlags({});
      setNewsFlagRatings({});
    }
  }, [watchlistStocks]);

  const watchlistSymbolIds = useMemo(
    () => (watchlistStocks || []).map((s) => s.symbol_id).filter(Boolean),
    [watchlistStocks]
  );

  useLiveMarketDisplay({
    enabled: Boolean(user) && watchlistSymbolIds.length > 0,
    symbolIds: watchlistSymbolIds,
    onApply: (live) => {
      setWatchlistStocks((prev) => {
        const next = applyLiveQuotesToStocks(prev, live.quotes, live.extendedQuotes);
        processWatchlistSummary(next);
        return next;
      });
    }
  });

  const fetchNewsFlags = async (symbolIds) => {
    if (!symbolIds || symbolIds.length === 0) return;
    
    try {
      // Get current date in YYYY-MM-DD format
      const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York'});
      
      // Use shared utility to get cached news data
      const newsData = await getCachedNewsData(currentDate, true);
      
      // Process news data to get flags and ratings
      const { flags, ratings } = processNewsFlags(newsData, symbolIds);
      
      setNewsFlags(flags);
      setNewsFlagRatings(ratings);
    } catch (error) {
      console.error('Error fetching news flags:', error);
      // On error, set all flags to grey
      const flags = {};
      const ratings = {};
      symbolIds.forEach((id) => {
        flags[id] = 'grey';
        ratings[id] = null;
      });
      setNewsFlags(flags);
      setNewsFlagRatings(ratings);
    }
  };

  const fetchWatchlistMap = useCallback(async (userEmail, forceRefresh = false) => {
    try {
      if (!forceRefresh) {
        const cachedMap = getCachedWatchlistMap(userEmail);
        if (cachedMap && Object.keys(cachedMap).length > 0) {
          setWatchlistMap(cachedMap);
          const names = Object.keys(cachedMap);
          setWatchlistNames(names);

          if (!activeWatchlist || !names.includes(activeWatchlist)) {
            if (names.length > 0) {
              setActiveWatchlist(names[0]);
            } else {
              setActiveWatchlist(null);
            }
          }

          return cachedMap;
        }
      }

      const response = await authFetch(`/api/watchlist/${userEmail}`);
      if (!response.ok) {
        console.error('Error fetching watchlist map:', response.status, await response.text());
        return {};
      }
      const data = await response.json();
      const map = data.watchlistMap || {};

      if (Object.keys(map).length > 0) {
        setCachedWatchlistMap(userEmail, map);
      } else {
        clearCachedWatchlistMap(userEmail);
      }

      setWatchlistMap(map);
      const names = Object.keys(map);
      setWatchlistNames(names);

      if (!activeWatchlist || !names.includes(activeWatchlist)) {
        if (names.length > 0) {
          setActiveWatchlist(names[0]);
        } else {
          setActiveWatchlist(null);
        }
      }
      return map;
    } catch (error) {
      console.error('Error fetching watchlist map:', error);
      return {};
    }
  }, [activeWatchlist]);

  const fetchWatchlist = async (userEmail, watchlistName, forceRefresh = false) => {
    if (!watchlistName) return;

    try {
      if (!forceRefresh) {
        const cachedWatchlist = getCachedWatchlist(userEmail, watchlistName);
        if (cachedWatchlist !== null) {
          console.log(`[WATCHLIST CACHE] Using cached watchlist for ${watchlistName} (${cachedWatchlist.length} items)`);
          setWatchlist(cachedWatchlist);
          return;
        }
      }

      const response = await authFetch(`/api/watchlist/${userEmail}?name=${watchlistName}`);
      const data = await response.json();
      const watchlist = data.watchlist || [];
      setWatchlist(watchlist);

      const cachedMap = getCachedWatchlistMap(userEmail) || {};
      const updatedMap = { ...cachedMap, [watchlistName]: watchlist };
      setCachedWatchlistMap(userEmail, updatedMap);
    } catch (error) {
      console.error('Error fetching watchlist:', error);
    }
  };

  const fetchWatchlistStocks = async (forceRefresh = false) => {
    // Don't fetch if watchlist is empty
    if (!watchlist || watchlist.length === 0) {
      setWatchlistStocks([]);
      setWatchlistSummary({ ...EMPTY_WATCHLIST_SUMMARY });
      return;
    }

    if (!user || !activeWatchlist) return;

    try {
      if (!forceRefresh) {
        const cachedWatchlistData = getCachedWatchlistData(user.email, activeWatchlist);
        if (cachedWatchlistData) {
          console.log(`[WATCHLIST DATA CACHE] Using cached watchlist stocks data for ${activeWatchlist}`);
          setWatchlistStocks(cachedWatchlistData.watchlistStocks || []);
          setWatchlistSummary(cachedWatchlistData.watchlistSummary || { ...EMPTY_WATCHLIST_SUMMARY });
          return;
        }
      }

      await fetchFreshWatchlistStocks(forceRefresh);
    } catch (error) {
      console.error('Error fetching watchlist stocks:', error);
    }
  };

  const fetchAllTradeDates = async () => {
    try {
      const dates = await getCachedTradeDates();
      setAllTradeDates(dates);
    } catch (error) {
      console.error('Error fetching trade dates:', error);
    }
  };

  const fetchFreshWatchlistStocks = async (forceRefresh = false) => {
    if (!user || !activeWatchlist || !watchlist || watchlist.length === 0) return;

    try {
      const display = await buildWatchlistDisplayData(watchlist, { forceRefresh });
      if (!display) {
        console.log('[WATCHLIST DATA] Could not build US/CA stock display data');
        return;
      }

      const { watchlistStocks, watchlistSummary, tradeDateKey } = display;

      setWatchlistStocks(watchlistStocks);
      setWatchlistSummary(watchlistSummary);

      if (watchlistStocks.length === 0 && watchlist.length > 0) {
        console.log('[WATCHLIST DATA CACHE] Watchlist has symbols but no matching stocks found, skipping cache write');
        return;
      }

      const watchlistData = {
        watchlist,
        watchlistStocks,
        watchlistSummary,
      };
      setCachedWatchlistDataWithDate(
        user.email,
        activeWatchlist,
        watchlistData,
        tradeDateKey || getLatestTradeDateKeySync()
      );
    } catch (error) {
      console.error('Error fetching fresh watchlist stocks:', error);
    }
  };

  const processWatchlistSummary = (stocks) => {
    const summary = computeWatchlistSummary(stocks);
    setWatchlistSummary(summary);
    return summary;
  };

  const addSymbols = async () => {
    if (!newSymbols.trim() || !user || !activeWatchlist) return;
    const tokens = newSymbols.split(',').map((s) => s.trim()).filter((t) => t.length > 0);
    const { getCachedSymbolNamesSync } = require('../utils/symbolNamesCache');
    const symbolMap = getCachedSymbolNamesSync();
    const symbolsArray = tokens.map((t) => resolveSymbolIdFromMap(t, symbolMap || {}) || t);
    if (symbolMap) {
      const invalidSymbols = symbolsArray.filter((id) => !symbolMap[id]);
      if (invalidSymbols.length > 0) {
        alert(`Invalid symbol_ids: ${invalidSymbols.join(', ')}. Please select valid symbols from suggestions.`);
        return;
      }
    }

    // Client-side limit check: max 10 symbols per watchlist
    const currentWatchlist = Array.isArray(watchlist) ? watchlist : [];
    const uniqueNewSymbols = symbolsArray.filter((symbol) => !currentWatchlist.includes(symbol));
    if (currentWatchlist.length + uniqueNewSymbols.length > MAX_SYMBOLS_PER_WATCHLIST) {
      alert(`Maximum of ${MAX_SYMBOLS_PER_WATCHLIST} symbols per watchlist.`);
      return;
    }
    
    setIsUpdating(true);
    try {
      const response = await authFetch(`/api/watchlist/${user.email}/add-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: activeWatchlist, symbols: symbolsArray }),
      });
      const data = await response.json();
      if (response.ok) {
        setNewSymbols('');
        setWatchlist(data.watchlist);
        const updatedMap = data.watchlistMap || watchlistMap;
        setWatchlistMap(updatedMap);
        setWatchlistNames(Object.keys(updatedMap));
        
        if (user && updatedMap) {
          updateCachedWatchlistMap(user.email, updatedMap);
        }
        if (user && activeWatchlist) {
          clearCachedWatchlistData(user.email, activeWatchlist);
          fetchWatchlist(user.email, activeWatchlist, true);
        }
      } else {
        console.error('Failed to add symbols:', data);
        alert(`Failed to add symbols: ${data.message}`);
      }
    } catch (error) {
      console.error('Error adding symbols:', error);
      alert(`Error adding symbols: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const removeSymbols = async () => {
    if (!newSymbols.trim() || !user || !activeWatchlist) return;
    const tokens = newSymbols.split(',').map((s) => s.trim()).filter((t) => t.length > 0);
    const { getCachedSymbolNamesSync } = require('../utils/symbolNamesCache');
    const symbolMap = getCachedSymbolNamesSync();
    const symbolsArray = tokens.map((t) => resolveSymbolIdFromMap(t, symbolMap || {}) || t);
    setIsUpdating(true);
    try {
      const response = await authFetch(`/api/watchlist/${user.email}/remove-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: activeWatchlist, symbols: symbolsArray }),
      });
      const data = await response.json();
      if (response.ok) {
        setNewSymbols('');
        setWatchlist(data.watchlist);
        const updatedMap = data.watchlistMap || watchlistMap;
        setWatchlistMap(updatedMap);
        setWatchlistNames(Object.keys(updatedMap));
        
        if (user && updatedMap) {
          updateCachedWatchlistMap(user.email, updatedMap);
        }
        if (user && activeWatchlist) {
          clearCachedWatchlistData(user.email, activeWatchlist);
          fetchWatchlist(user.email, activeWatchlist, true);
        }
      } else {
        alert('Failed to remove symbols');
      }
    } catch (error) {
      console.error('Error removing symbols:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateWatchlist = async () => {
    if (!user) return;

    if (watchlistNames.length >= MAX_WATCHLISTS) {
      alert(`Maximum of ${MAX_WATCHLISTS} watchlists allowed.`);
      return;
    }
    
    // Generate default name if not provided
    let watchlistName = newWatchlistName.trim();
    if (!watchlistName) {
      const existingNames = watchlistNames.filter(n => n.startsWith('watchlist_'));
      const nextNum = existingNames.length > 0 
        ? Math.max(...existingNames.map(n => {
            const match = n.match(/watchlist_(\d+)/);
            return match ? parseInt(match[1]) : 0;
          })) + 1
        : 1;
      watchlistName = `watchlist_${nextNum}`;
    }

    try {
      const response = await authFetch(`/api/watchlist/${user.email}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: watchlistName }),
      });
      const data = await response.json();
      if (response.ok) {
        setNewWatchlistName('');
        setShowCreateModal(false);
        const updatedMap = data.watchlistMap || {};
        
        // Update cache after successful create
        if (updatedMap) {
          updateCachedWatchlistMap(user.email, updatedMap);
        }
        
        await fetchWatchlistMap(user.email);
        setActiveWatchlist(watchlistName);
      } else {
        alert(`Failed to create watchlist: ${data.message}`);
      }
    } catch (error) {
      console.error('Error creating watchlist:', error);
      alert(`Error creating watchlist: ${error.message}`);
    }
  };

  const handleDeleteWatchlist = async () => {
    if (!watchlistToDelete || !user) return;

    try {
      const response = await authFetch(`/api/watchlist/${user.email}/${watchlistToDelete}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (response.ok) {
        const updatedMap = data.watchlistMap || {};
        
        // Update cache after successful delete
        if (updatedMap) {
          updateCachedWatchlistMap(user.email, updatedMap);
        }
        
        await fetchWatchlistMap(user.email);
        setShowDeleteConfirm(false);
        setWatchlistToDelete(null);
        if (watchlistNames.length <= 1) {
          setActiveWatchlist(null);
        }
      } else {
        alert(`Failed to delete watchlist: ${data.message}`);
      }
    } catch (error) {
      console.error('Error deleting watchlist:', error);
      alert(`Error deleting watchlist: ${error.message}`);
    }
  };

  const handleWatchlistTabChange = (watchlistName) => {
    setActiveWatchlist(watchlistName);
    setPage(1);
    if (user) {
      fetchWatchlist(user.email, watchlistName);
    }
  };

  const handleRefresh = async () => {
    if (!user || isRefreshing) return;
    setIsRefreshing(true);
    try {
      clearPwStockIndexCacheAndStocksMemory();
      watchlistNames.forEach(name => clearCachedWatchlistData(user.email, name));
      const freshMap = await fetchWatchlistMap(user.email, true);
      const allNames = Object.keys(freshMap || {});
      await Promise.all(allNames.map(name => fetchWatchlist(user.email, name, true)));
      if (activeWatchlist) {
        await fetchWatchlistStocks(true);
      }
    } catch (error) {
      console.error('Error refreshing watchlist:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const isWatchlistEmpty = watchlist && watchlist.length === 0;
  const canCreateWatchlist = watchlistNames.length < MAX_WATCHLISTS; // Show create until max watchlists reached

  const filteredStocks = useMemo(() => {
    const q = (tableSearch || '').trim().toLowerCase();
    if (!q) return watchlistStocks || [];
    return (watchlistStocks || []).filter((s) =>
      (s.symbol || '').toLowerCase().includes(q) ||
      (s.name || '').toLowerCase().includes(q) ||
      (s.sector || '').toLowerCase().includes(q)
    );
  }, [watchlistStocks, tableSearch]);

  const sortedStocks = useMemo(() => {
    const list = filteredStocks || [];
    if (sortDirection === 'neutral') return list;
    const col = WATCHLIST_COLUMNS.find((c) => c.key === sortField);
    const type = col?.type || 'string';
    return [...list].sort((a, b) => {
      const primary = compareSortValues(a[sortField], b[sortField], type, sortDirection);
      if (primary !== 0) return primary;
      return compareSortValues(a.symbol, b.symbol, 'string', 'asc');
    });
  }, [filteredStocks, sortField, sortDirection]);

  const totalPages = Math.ceil(sortedStocks.length / itemsPerPage);

  useEffect(() => {
    setPage(1);
  }, [sortedStocks.length, tableSearch]);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [page, totalPages]);

  const paginatedStocks = sortedStocks.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const watchlistCounts = useMemo(() => {
    const counts = {};
    watchlistNames.forEach((name) => {
      const arr = watchlistMap?.[name];
      counts[name] = Array.isArray(arr) ? arr.length : 0;
    });
    return counts;
  }, [watchlistNames, watchlistMap]);

  const renderSortHeader = (col, label, className = '') => {
    const isActive = sortDirection !== 'neutral' && sortField === col.key;
    return (
      <th className={className || undefined} aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <span className="screener-sort-header-inner">
          {label || col.label}
          <button type="button" className="screener-sort-trigger" onClick={() => handleColumnSort(col.key)} aria-label={`Sort by ${label || col.label}`}>
            <span className={`screener-sort-arrow screener-sort-arrow--up ${isActive && sortDirection === 'asc' ? 'screener-sort-icon--active' : 'screener-sort-icon--muted'}`} aria-hidden="true" />
            <span className={`screener-sort-arrow screener-sort-arrow--down ${isActive && sortDirection === 'desc' ? 'screener-sort-icon--active' : 'screener-sort-icon--muted'}`} aria-hidden="true" />
          </button>
        </span>
      </th>
    );
  };

  const colByKey = (key) => WATCHLIST_COLUMNS.find((c) => c.key === key);

  const formatPercentage = (value) => {
    return value != null ? `${Number(value).toFixed(2)}%` : 'N/A';
  };

  const getNewsFlagTooltip = (flagColor) => {
    switch (flagColor) {
      case 'red':
        return 'Related news found with sentiment rating lte 4/10';
      case 'green':
        return 'Related news found with sentiment rating gte 7/10';
      case 'amber':
        return 'Related news found with sentiment rating eq 5/10|6/10';
      case 'grey':
      default:
        return 'No related news in cached latest news';
    }
  };

  const formatMarketCap = (marketCap) => {
    if (marketCap == null) return '--';
    if (marketCap >= 1000000000000) {
      return `${(marketCap / 1000000000000).toFixed(2)}T`;
    } else if (marketCap >= 1000000000) {
      return `${(marketCap / 1000000000).toFixed(2)}B`;
    } else if (marketCap >= 1000000) {
      return `${(marketCap / 1000000).toFixed(2)}M`;
    } else if (marketCap >= 1000) {
      return `${(marketCap / 1000).toFixed(2)}K`;
    } else {
      return `${marketCap.toFixed(2)}`;
    }
  };

  const formatFixed = (n, digits = 2) => {
    if (n == null) return '--';
    const num = Number(n);
    return Number.isFinite(num) ? num.toFixed(digits) : '--';
  };

  const renderPctChange = (value) => (
    <span className={`pw-change ${value > 0 ? 'positive' : value < 0 ? 'negative' : ''}`}>
      {value != null ? (value > 0 ? '+' : '') + Number(value).toFixed(2) + '%' : '--'}
    </span>
  );

  const getSignalStatus = (item) => item.new_status || item[SIGNAL_FIELD];
  const getSignalClass = (item) => {
    const status = (getSignalStatus(item) || '').toLowerCase();
    if (status === 'buy') return 'pw-status-buy';
    if (status === 'sell') return 'pw-status-sell';
    return 'pw-status-hold';
  };

  const getSentimentClass = (sentiment) => {
    const s = (sentiment || '').toLowerCase().trim();
    if (s === 'bullish') return 'pw-status-buy';
    if (s === 'bearish') return 'pw-status-sell';
    return 'pw-status-hold';
  };

  if (!user) {
    return (
        <div className="page-content watchlist-page pw-page">
          <div className="pw-login-prompt">
            <Eye size={48} />
            <h2>Manage Your Watchlist</h2>
            <p>Please <Link to="/login">log in</Link> to view and manage your watchlist.</p>
          </div>
        </div>
    );
  }

  if (isLoadingWatchlistMap) {
    return (
        <div className="page-content watchlist-page pw-page">
          <div className="pw-header">
            <div className="pw-header-left">
              <div className="pw-header-text">
                <h2 className="pw-title">
                  <Eye size={24} className="pw-title-icon pw-title-icon--watchlist" />
                  <span>My Watchlist</span>
                </h2>
              </div>
            </div>
          </div>
          <div className="pw-loading">
            <div className="pw-spinner"></div>
            <p>Loading watchlist...</p>
          </div>
        </div>
    );
  }

  if (watchlistNames.length === 0) {
    return (
        <div className="page-content watchlist-page pw-page">
          <div className="pw-header">
            <div className="pw-header-left">
              <div className="pw-header-text">
                <h2 className="pw-title">
                  <Eye size={24} className="pw-title-icon pw-title-icon--watchlist" />
                  <span>My Watchlist</span>
                </h2>
              </div>
            </div>
          </div>
          <div className="pw-empty-list">
            <Eye size={44} />
            <h3>No Watchlists Yet</h3>
            <p>Create your first watchlist to start tracking stocks.</p>
            <button onClick={() => setShowCreateModal(true)} className="pw-btn pw-btn-primary">
              <Plus size={16} /> Create Watchlist
            </button>
          </div>
          
          {showCreateModal && (
            <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
              <div className="pw-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pw-modal-content">
                  <h3>Create New Watchlist</h3>
                  <p className="pw-modal-description">Give your watchlist a name, or leave empty for a default.</p>
                  <input
                    type="text"
                    value={newWatchlistName}
                    onChange={(e) => setNewWatchlistName(e.target.value)}
                    placeholder="e.g. Tech Stocks, Small Caps..."
                    className="pw-modal-input"
                  />
                  <div className="pw-modal-actions">
                    <button onClick={() => setShowCreateModal(false)} className="pw-btn pw-btn-ghost">
                      Cancel
                    </button>
                    <button onClick={handleCreateWatchlist} className="pw-btn pw-btn-primary">
                      Create
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
    );
  }

  const currentWatchlistCount = Array.isArray(watchlist) ? watchlist.length : 0;
  const avg6MonthPerformance = watchlistSummary.avg6MonthPerformance != null
    ? watchlistSummary.avg6MonthPerformance
    : (watchlistStocks?.length
        ? watchlistStocks.reduce((sum, s) => sum + (s[PCT_6M_FIELD] ?? 0), 0) / watchlistStocks.length
        : 0);
  const avg3MonthPerformance = watchlistSummary.avg3MonthPerformance != null
    ? watchlistSummary.avg3MonthPerformance
    : (watchlistStocks?.length
        ? watchlistStocks.reduce((sum, s) => sum + (s[PCT_3M_FIELD] ?? 0), 0) / watchlistStocks.length
        : 0);
  const avg1MonthPerformance = watchlistSummary.avg1MonthPerformance ?? watchlistSummary.avg4WeekPerformance ?? 0;

  return (
    <div className="page-content watchlist-page pw-page">
      <div className="pw-header">
          <div className="pw-header-left">
            <div className="pw-header-text">
              <h2 className="pw-title">
                <Eye size={24} className="pw-title-icon pw-title-icon--watchlist" />
                <span>My Watchlist</span>
              </h2>
              <p className="pw-subtitle">Monitor stocks you&rsquo;re interested in</p>
            </div>
          </div>
          <div className="pw-header-actions">
            <button
              onClick={handleRefresh}
              className="pw-icon-btn"
              disabled={isRefreshing}
              title="Refresh watchlist data"
              aria-label="Refresh watchlist data"
            >
              <RefreshCw size={24} className={isRefreshing ? 'spinning' : ''} />
            </button>
            {canCreateWatchlist && (
              <button onClick={() => setShowCreateModal(true)} className="pw-btn pw-btn-primary">
                <Plus size={16} /> New Watchlist
              </button>
            )}
            {activeWatchlist && watchlistNames.length > 0 && (
              <button
                onClick={() => {
                  setWatchlistToDelete(activeWatchlist);
                  setShowDeleteConfirm(true);
                }}
                className="pw-btn pw-btn-ghost-danger"
                title="Delete current watchlist"
                aria-label="Delete current watchlist"
              >
                <Trash2 size={16} />
                <span className="pw-btn-label-collapse">Delete</span>
              </button>
            )}
          </div>
        </div>

        {watchlistNames.length > 0 && (
          <div className="pw-tabs" role="tablist" aria-label="Watchlists">
            {watchlistNames.map(name => (
              <button
                key={name}
                role="tab"
                aria-selected={activeWatchlist === name}
                className={`pw-tab ${activeWatchlist === name ? 'active' : ''}`}
                onClick={() => handleWatchlistTabChange(name)}
              >
                <span className="pw-tab-name">
                  {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <span className="pw-tab-count">{watchlistCounts[name] ?? 0}</span>
              </button>
            ))}
            <span className="pw-tabs-meta">{watchlistNames.length}/{MAX_WATCHLISTS} lists</span>
          </div>
        )}

        <div className="pw-hero-card pw-hero-card--watchlist">
          <div className="pw-hero-main">
            <div className="pw-hero-label">
              <Activity size={14} />
              <span>Watchlist Performance</span>
            </div>
            <div className={`pw-hero-value ${avg6MonthPerformance >= 0 ? 'positive' : 'negative'}`}>
              {avg6MonthPerformance >= 0 ? '+' : ''}{formatPercentage(avg6MonthPerformance)}
            </div>
            <div className="pw-hero-pl-suffix">Avg 6M Change · {watchlistStocks?.length || 0} symbols tracked</div>
          </div>
          <div className="pw-hero-stats">
            <div className="pw-hero-stat">
              <span className="pw-hero-stat-label">
                {watchlistSummary.avg1WeekPerformance >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {' '}% Change 1W
              </span>
              <span className={`pw-hero-stat-value ${watchlistSummary.avg1WeekPerformance >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(watchlistSummary.avg1WeekPerformance)}
              </span>
            </div>
            <div className="pw-hero-stat">
              <span className="pw-hero-stat-label">
                {avg1MonthPerformance >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {' '}% 1M Chng
              </span>
              <span className={`pw-hero-stat-value ${avg1MonthPerformance >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(avg1MonthPerformance)}
              </span>
            </div>
            <div className="pw-hero-stat">
              <span className="pw-hero-stat-label">
                {avg3MonthPerformance >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {' '}% 3M Chng
              </span>
              <span className={`pw-hero-stat-value ${avg3MonthPerformance >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(avg3MonthPerformance)}
              </span>
            </div>
          </div>
        </div>

        <div className={`pw-management-card ${showManagePanel ? 'open' : 'collapsed'}`}>
          <button
            type="button"
            className="pw-management-toggle"
            onClick={() => setShowManagePanel((p) => !p)}
            aria-expanded={showManagePanel}
          >
            <span className="pw-management-toggle-label">
              <Plus size={16} /> Manage Symbols
            </span>
            <span className="pw-management-toggle-meta">{currentWatchlistCount}/{MAX_SYMBOLS_PER_WATCHLIST} used</span>
          </button>
          {showManagePanel && (
            <div className="pw-form-row pw-form-row--watchlist">
              <div className="watchlist-symbol-input-wrapper">
                <MultiSymbolAutocomplete
                  value={newSymbols}
                  onChange={setNewSymbols}
                  placeholder="Type symbols (comma-separated)…"
                  className="symbol-input"
                  disabled={isUpdating}
                  maxSuggestions={10}
                />
              </div>
              <div className="pw-btn-group">
                <button onClick={addSymbols} className="pw-btn pw-btn-primary pw-btn-sm" disabled={isUpdating}>
                  <Plus size={16} /> Add
                </button>
                <button onClick={removeSymbols} className="pw-btn pw-btn-outline-danger pw-btn-sm" disabled={isUpdating}>
                  <Minus size={16} /> Remove
                </button>
              </div>
            </div>
          )}
        </div>

        {isLoadingWatchlist ? (
          <div className="pw-loading">
            <div className="pw-spinner"></div>
            <p>Loading watchlist data...</p>
          </div>
        ) : isWatchlistEmpty ? (
          <div className="pw-empty-state">
            <Eye size={40} />
            <h3>No Stocks Yet</h3>
            <p>Add symbols above to start monitoring stocks.</p>
          </div>
        ) : (
          <div className="pw-data-card">
            <div className="pw-data-header">
              <div className="pw-data-header-left">
                <h3>Watching</h3>
                <span className="pw-data-count">{watchlistStocks?.length || 0} symbols</span>
              </div>
              <div className="pw-data-header-right">
                <div className="pw-search-wrap">
                  <Search size={14} className="pw-search-icon" />
                  <input
                    type="text"
                    className="pw-search-input"
                    placeholder="Search symbol, company, sector..."
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                  />
                  {tableSearch && (
                    <button type="button" className="pw-search-clear" onClick={() => setTableSearch('')} aria-label="Clear search">×</button>
                  )}
                </div>
              </div>
            </div>
            {paginatedStocks.length > 0 ? (
              <>
                <div className="pw-table-wrap">
                  <table className="pw-table pw-table--watchlist pw-table--sticky-symbol">
                    <thead>
                      <tr>
                        {renderSortHeader(colByKey('symbol'), undefined, 'pw-col-symbol')}
                        {renderSortHeader(colByKey('name'), undefined, 'pw-col-company')}
                        <th>Exchange</th>
                        {renderSortHeader(colByKey('sector'))}
                        {renderSortHeader(colByKey('market_cap'))}
                        {renderSortHeader(colByKey('close'))}
                        {renderSortHeader(colByKey('pct_change'))}
                        {renderSortHeader(colByKey('trade_timestamp'), 'Updated At')}
                        {renderSortHeader(colByKey('price_ext'), 'Price Ext.')}
                        {renderSortHeader(colByKey('pct_change_ext'), '% Change Ext')}
                        <th>Ccy</th>
                        {renderSortHeader(colByKey('volume'))}
                        <th title="Relative volume — 1 day vs 20-day avg">Rel Vol</th>
                        <th title="Latest news sentiment rating">News</th>
                        {renderSortHeader(colByKey(PCT_1W_FIELD), '% Change 1W')}
                        {renderSortHeader(colByKey(PCT_1M_FIELD), '% Change 1M')}
                        {renderSortHeader(colByKey(PCT_3M_FIELD), '% Change 3M')}
                        {renderSortHeader(colByKey(PCT_6M_FIELD), '% Change 6M')}
                        {renderSortHeader(colByKey(SIGNAL_FIELD), 'Signal')}
                        <th title="AI sentiment short-term outlook">AI Sentiment</th>
                        {renderSortHeader(colByKey('beta'))}
                        <th title="Float shares">Float</th>
                        <th title="Average True Range">ATR</th>
                        {renderSortHeader(colByKey('pe'))}
                        {renderSortHeader(colByKey('rsi'))}
                        <th title="20-day moving average">MA20</th>
                        <th title="50-day moving average">MA50</th>
                        <th title="200-day moving average">MA200</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedStocks.map((stock, index) => (
                        <tr key={index}>
                          <td className="pw-col-symbol">
                            <a href={`/stock/${encodeURIComponent(String(stock.symbol_id).toUpperCase())}`} target="_blank" rel="noopener noreferrer" className="pw-symbol-link">
                              {stock.symbol}
                            </a>
                          </td>
                          <td className="pw-company-name pw-col-company">{stock.name}</td>
                          <td>{stock.exchange || '--'}</td>
                          <td className="pw-company-name">{stock.sector}</td>
                          <td>{formatMarketCap(stock.market_cap)}</td>
                          <td>{stock.close != null ? stock.close.toFixed(2) : 'N/A'}</td>
                          <td>
                            <span className={`pw-change ${stock.pct_change > 0 ? 'positive' : stock.pct_change < 0 ? 'negative' : ''}`}>
                              {stock.pct_change != null ? (stock.pct_change > 0 ? '+' : '') + stock.pct_change.toFixed(2) + '%' : '--'}
                            </span>
                          </td>
                          <td>{stock.trade_timestamp || '--'}</td>
                          <td>{stock.price_ext != null ? `${Number(stock.price_ext).toFixed(2)}` : '--'}</td>
                          <td>
                            <span className={`pw-change ${stock.pct_change_ext > 0 ? 'positive' : stock.pct_change_ext < 0 ? 'negative' : ''}`}>
                              {stock.pct_change_ext != null ? (stock.pct_change_ext > 0 ? '+' : '') + Number(stock.pct_change_ext).toFixed(2) + '%' : '--'}
                            </span>
                          </td>
                          <td>{stock.currency}</td>
                          <td>{stock.volume}</td>
                          <td>{formatFixed(stock.average_volume != null ? stock.volume / stock.average_volume : null)}</td>
                          <td>
                            <div className="news-flag-container">
                              <span
                                className={`news-flag news-flag-${newsFlags[stock.symbol_id] || 'grey'}`}
                                title={getNewsFlagTooltip(newsFlags[stock.symbol_id] || 'grey')}
                              >
                                ●
                              </span>
                              {newsFlagRatings[stock.symbol_id] != null && (
                                <span className="news-rating-box" title="Latest news AI rating">
                                  {renderNewsRating(newsFlagRatings[stock.symbol_id])}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{renderPctChange(stock[PCT_1W_FIELD])}</td>
                          <td>{renderPctChange(stock[PCT_1M_FIELD])}</td>
                          <td>{renderPctChange(stock[PCT_3M_FIELD])}</td>
                          <td>{renderPctChange(stock[PCT_6M_FIELD])}</td>
                          <td>
                            <span className={`pw-status-badge ${getSignalClass(stock)}`}>
                              {getSignalStatus(stock)}
                            </span>
                          </td>
                          <td>
                            {stock.short_term_sentiment ? (
                              <span className={`pw-status-badge ${getSentimentClass(stock.short_term_sentiment)}`}>
                                {stock.short_term_sentiment}
                              </span>
                            ) : (
                              ''
                            )}
                          </td>
                          <td>{stock.beta}</td>
                          <td>{formatMarketCap(stock.float_shares)}</td>
                          <td>{stock.atr}</td>
                          <td>{formatFixed(stock.pe)}</td>
                          <td>{formatFixed(stock.rsi)}</td>
                          <td>{formatFixed(stock.sma20_prc)}</td>
                          <td>{formatFixed(stock.sma50_prc)}</td>
                          <td>{formatFixed(stock.sma200_prc)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pw-mobile-list">
                  {paginatedStocks.map((stock, index) => (
                    <div key={`m-${index}`} className="pw-mobile-row">
                      <div className="pw-mobile-row-top">
                        <div className="pw-mobile-symbol">
                          <a href={`/stock/${encodeURIComponent(String(stock.symbol_id).toUpperCase())}`} target="_blank" rel="noopener noreferrer" className="pw-symbol-link">{stock.symbol}</a>
                          <span className="pw-mobile-name">{stock.name}</span>
                        </div>
                        <div className={`pw-mobile-price ${(stock[PCT_6M_FIELD] ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                          <span className="pw-mobile-price-val">{stock.close != null ? stock.close.toFixed(2) : 'N/A'}</span>
                          <span className="pw-mobile-price-pct">
                            {stock[PCT_6M_FIELD] != null ? (stock[PCT_6M_FIELD] > 0 ? '+' : '') + stock[PCT_6M_FIELD].toFixed(2) + '%' : '--'}
                          </span>
                        </div>
                      </div>
                      <div className="pw-mobile-row-grid">
                        <div className="pw-mobile-cell"><span>Sector</span><strong>{stock.sector || '–'}</strong></div>
                        <div className="pw-mobile-cell"><span>Market Cap</span><strong>{formatMarketCap(stock.market_cap)}</strong></div>
                        <div className="pw-mobile-cell"><span>Volume</span><strong>{stock.volume}</strong></div>
                        <div className="pw-mobile-cell">
                          <span>Signal</span>
                          <span className={`pw-status-badge ${getSignalClass(stock)}`}>{getSignalStatus(stock)}</span>
                        </div>
                        <div className="pw-mobile-cell"><span>% 1W</span><strong>{renderPctChange(stock[PCT_1W_FIELD])}</strong></div>
                        <div className="pw-mobile-cell"><span>% 1M</span><strong>{renderPctChange(stock[PCT_1M_FIELD])}</strong></div>
                        <div className="pw-mobile-cell"><span>% 3M</span><strong>{renderPctChange(stock[PCT_3M_FIELD])}</strong></div>
                        <div className="pw-mobile-cell"><span>% 6M</span><strong>{renderPctChange(stock[PCT_6M_FIELD])}</strong></div>
                        <div className="pw-mobile-cell"><span>PE</span><strong>{formatFixed(stock.pe)}</strong></div>
                        <div className="pw-mobile-cell"><span>RSI</span><strong>{formatFixed(stock.rsi)}</strong></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pw-pagination">
                  <button onClick={() => setPage(1)} disabled={page === 1} title="First Page" aria-label="First Page">
                    <ChevronsLeft size={18} />
                  </button>
                  <button onClick={() => setPage(prev => Math.max(prev - 1, 1))} disabled={page === 1} title="Previous Page" aria-label="Previous Page">
                    <ChevronLeft size={18} />
                  </button>
                  <span className="pw-page-info">Page {page} of {totalPages}</span>
                  <button onClick={() => setPage(prev => Math.min(prev + 1, totalPages))} disabled={page === totalPages} title="Next Page" aria-label="Next Page">
                    <ChevronRight size={18} />
                  </button>
                  <button onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Last Page" aria-label="Last Page">
                    <ChevronsRight size={18} />
                  </button>
                </div>
              </>
            ) : (
              <div className="pw-empty-state pw-empty-state--inline">
                <Search size={32} />
                <h3>No matches</h3>
                <p>{tableSearch ? `No stocks match "${tableSearch}"` : 'No stocks to display.'}</p>
                {tableSearch && (
                  <button onClick={() => setTableSearch('')} className="pw-btn pw-btn-ghost pw-btn-sm">Clear search</button>
                )}
              </div>
            )}
          </div>
        )}
        
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="pw-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pw-modal-content">
                <h3>Create New Watchlist</h3>
                <p className="pw-modal-description">Give your watchlist a name, or leave empty for a default.</p>
                <input
                  type="text"
                  value={newWatchlistName}
                  onChange={(e) => setNewWatchlistName(e.target.value)}
                  placeholder="e.g. Tech Stocks, Small Caps..."
                  className="pw-modal-input"
                />
                <div className="pw-modal-actions">
                  <button onClick={() => setShowCreateModal(false)} className="pw-btn pw-btn-ghost">
                    Cancel
                  </button>
                  <button onClick={handleCreateWatchlist} className="pw-btn pw-btn-primary">
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="pw-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pw-modal-content">
                <h3>Delete Watchlist</h3>
                <p className="pw-modal-description">
                  Are you sure you want to delete <strong>"{watchlistToDelete}"</strong>? This action cannot be undone.
                </p>
                <div className="pw-modal-actions">
                  <button onClick={() => setShowDeleteConfirm(false)} className="pw-btn pw-btn-ghost">
                    Cancel
                  </button>
                  <button onClick={handleDeleteWatchlist} className="pw-btn pw-btn-danger">
                    Delete Watchlist
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default Watchlist;
